import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import SectionWrapper from "../components/SectionWrapper";
import StepProgress from "../components/ui/StepProgress";
import Modal from "../components/ui/Modal";
import { delhiNcrPincodes } from "../data/healthcareContent";
import { bookAppointment, getBookings } from "../api/bookingApi";

const formatDateTimeLocal = (date = new Date()) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const isValidVisitTiming = (value) => {
  if (!value) return false;

  const selectedDate = new Date(value);
  if (Number.isNaN(selectedDate.getTime())) return false;

  const selectedMinute = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate(),
    selectedDate.getHours(),
    selectedDate.getMinutes()
  ).getTime();

  const now = new Date();
  const currentMinute = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes()
  ).getTime();

  return selectedMinute >= currentMinute;
};

const BookDoctorConsultantPage = () => {
  const location = useLocation();
  const initialBookingType = location.state?.bookingType || "";
  const defaultVisitPrice = initialBookingType === "home" ? 5000 : 1100;
  const [stepIndex, setStepIndex] = useState(0);
  const [successOpen, setSuccessOpen] = useState(false);
  const [mobileError, setMobileError] = useState("");
  const [, setBookings] = useState([]);

  const [formData, setFormData] = useState({
    bookingType: initialBookingType,
    doctorId: "",
    department: "",
    date: "",
    slot: "",
    consultationMode: "Online",
    specialist: "",
    addressLine: "",
    locality: "",
    city: "",
    pincode: "",
    visitTiming: "",
    visitPrice: defaultVisitPrice,
    emergency: false,
    patientName: "",
    patientAge: "",
    gender: "",
    mobile: "",
    notes: "",
  });
  const displayedVisitPrice =
    formData.bookingType === "home" ? 5000 : 1100;
  const homeVisitMinTiming = formatDateTimeLocal(new Date());

  const stepLabels = useMemo(() => {
    if (formData.bookingType === "home") return ["Schedule", "Patient", "Confirm"];
    return ["Patient", "Confirm"];
  }, [formData.bookingType]);

  const currentStepKey = stepLabels[stepIndex] || "Patient";

  useEffect(() => {
    setStepIndex((prev) => Math.min(prev, Math.max(stepLabels.length - 1, 0)));
  }, [stepLabels.length]);

  const isNcrPincode = true;

  const loadBookings = async () => {
    const response = await getBookings();

    if (response?.Success !== false) {
      const items = Array.isArray(response)
        ? response
        : response?.Data ?? response?.data ?? [];
      setBookings(items);
    }
  };

  useEffect(() => {
    const fetchBookings = async () => {
      await loadBookings();
    };

    fetchBookings();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue =
      name === "mobile" ? value.replace(/\D/g, "").slice(0, 10) : value;

    if (name === "mobile" && isValidMobile(nextValue)) {
      setMobileError("");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : nextValue,
    }));
  };

  const handleVisitTimingChange = (event) => {
    const { value } = event.target;

    if (value && !isValidVisitTiming(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      visitTiming: value,
    }));
  };

  const preventManualDateEntry = (event) => {
    const allowedKeys = new Set([
      "Tab",
      "Shift",
      "Enter",
      "Escape",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "PageUp",
      "PageDown",
      "Control",
      "Meta",
      "Alt",
    ]);

    if (allowedKeys.has(event.key)) return;

    if (event.ctrlKey || event.metaKey || event.altKey) return;

    event.preventDefault();
  };

  const isValidMobile = (mobile) => /^\d{10}$/.test(mobile);

  const validateStep = () => {
    if (!formData.bookingType) return false;

    if (currentStepKey === "Schedule" && formData.bookingType === "home") {
      return Boolean(
        formData.addressLine &&
        formData.locality &&
        formData.city &&
        formData.pincode &&
        formData.visitTiming &&
        isValidVisitTiming(formData.visitTiming) &&
        // formData.specialist &&
        isNcrPincode
      );
    }

    if (currentStepKey === "Patient") {
      return Boolean(
        formData.patientName &&
        formData.patientAge &&
        isValidMobile(formData.mobile) &&
        (formData.bookingType !== "daily" || isValidVisitTiming(formData.visitTiming)) &&
        formData.visitPrice
      );
    }

    return true;
  };

  const handleNext = () => {
    if (!validateStep()) {
      if (currentStepKey === "Patient" && !isValidMobile(formData.mobile)) {
        setMobileError("Mobile number must be exactly 10 digits.");
      }
      alert("Please complete required details for this step.");
      return;
    }
    if (currentStepKey === "Patient") {
      setMobileError("");
    }
    setStepIndex((prev) => Math.min(prev + 1, stepLabels.length - 1));
  };

  const handleBack = () => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleConfirm = async () => {
    if (!validateStep()) {
      if (currentStepKey === "Patient" && !isValidMobile(formData.mobile)) {
        setMobileError("Mobile number must be exactly 10 digits.");
      }
      alert("Please review the booking details before confirming.");
      return;
    }

    const response = await bookAppointment(formData);

    if (response?.Success !== false) {
      await loadBookings();
      setSuccessOpen(true);
      setStepIndex(0);
      setFormData({
        bookingType: formData.bookingType,
        doctorId: "",
        department: "",
        date: "",
        slot: "",
        consultationMode: "Online",
        // specialist: "",
        addressLine: "",
        locality: "",
        city: "",
        pincode: "",
        visitTiming: "",
        visitPrice: defaultVisitPrice,
        emergency: false,
        patientName: "",
        patientAge: "",
        gender: "",
        mobile: "",
        notes: "",
      });
      setMobileError("");
    } else {
      alert(response?.Message || "Unable to submit booking right now.");
    }
  };

  return (
    <main className="mt-20 bg-cream pb-24 md:pb-16">
      <SectionWrapper className="py-10 pb-0">
        <div className="rounded-3xl bg-gradient-to-r from-maroon to-[#2f5475] p-8 text-white shadow-xl">
          <p className="mb-3 text-sm uppercase tracking-[0.22em] text-white/80">Appointment Journey</p>
          <h1 className="mb-3 text-3xl font-bold md:text-4xl">Book Doctor Consultation</h1>
          <p className="max-w-3xl text-white/85">
            Multi-step booking flow for Daily Consultation and Home Visit, designed for quick, reliable family care planning.
          </p>
        </div>
      </SectionWrapper>

      <SectionWrapper className="pt-0 pb-2 md:pb-3">
        <StepProgress steps={stepLabels} currentStep={stepIndex + 1} />
      </SectionWrapper>

      <SectionWrapper className="pt-2 md:pt-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStepKey}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            className="rounded-2xl bg-white p-6 shadow-md md:p-8"
          >
            {currentStepKey === "Schedule" && formData.bookingType === "home" ? (
              <div>
                <h2 className="text-2xl font-bold text-maroon">Home Visit Scheduling</h2>
                <p className="mt-3 inline-flex rounded-full bg-rose-100 px-4 py-2 text-sm font-semibold text-rose-700">
                  Available Only in Delhi NCR
                </p>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <input
                    type="text"
                    name="addressLine"
                    value={formData.addressLine}
                    onChange={handleChange}
                    placeholder="Address Line"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <input
                    type="text"
                    name="locality"
                    value={formData.locality}
                    onChange={handleChange}
                    placeholder="Locality"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    maxLength={6}
                    placeholder="Delhi NCR Pincode"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  {/*                   
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Preferred Visit Timing
                    </label>

                    <input
                      type="datetime-local"
                      name="visitTiming"
                      value={formData.visitTiming}
                      onChange={handleVisitTimingChange}
                      onKeyDown={preventManualDateEntry}
                      onPaste={(event) => event.preventDefault()}
                      onDrop={(event) => event.preventDefault()}
                      min={homeVisitMinTiming}
                      inputMode="none"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Choose the date and time in one picker. Previous times are not allowed.
                    </p>
                  </div> */}
                   {/* preffered Timing */}
                  <div>

                      <label className="mb-2 block text-sm font-medium text-gray-700">
                      Preferred Visit Timing
                    </label>

                  <div className="relative">
                   
                    {!formData.visitTiming && (
                      <span className="pointer-events-none absolute inset-y-0 left-4 z-10 flex items-center justify-center text-gray-500">
                        Choose date and time
                      </span>
                    )}
                    <input
                      type="datetime-local"
                      placeholder="Choose date and time"
                      name="visitTiming"
                      value={formData.visitTiming}
                      onChange={handleVisitTimingChange}
                      onKeyDown={preventManualDateEntry}
                      onPaste={(event) => event.preventDefault()}
                      onDrop={(event) => event.preventDefault()}
                      min={homeVisitMinTiming}
                      onClick={(event) => event.currentTarget.showPicker?.()}
                      className={`date-time-input w-full cursor-pointer rounded-xl border border-gray-300 px-4 py-3 ${!formData.visitTiming ? "date-time-empty" : ""
                        }`}
                      required
                    />
                  </div>
                  </div>

                  {/* <select
                    name="specialist"
                    value={formData.specialist}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  >
                    <option value="">Specialist / Doctor Type</option>
                    {departments.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select> */}
                </div>

                <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="emergency"
                    checked={formData.emergency}
                    onChange={handleChange}
                  />
                  Emergency request
                </label>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-gray-200 bg-cream p-4">
                    <h3 className="text-lg font-semibold text-maroon">Visit Charges</h3>
                    <p className="mt-2 text-sm text-gray-700">Consultation + care coordination starting from</p>
                    <p className="mt-2 text-2xl font-bold text-maroon">Rs. 5000/-</p>
                  </div>
                  {/* <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-sky-50 to-white p-4">
                    <h3 className="text-lg font-semibold text-maroon">Location Preview (Demo)</h3>
                    <div className="mt-3 h-24 rounded-xl bg-[radial-gradient(circle_at_20%_20%,#93c5fd_2px,transparent_3px)] bg-[length:24px_24px] relative">
                      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-maroon px-2 py-1 text-xs text-white">
                        Visit Pin
                      </span>
                    </div>
                  </div> */}
                </div>
              </div>
            ) : null}

            {currentStepKey === "Patient" ? (
              <div>
                <h2 className="text-2xl font-bold text-maroon">Patient Details</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <input
                    type="text"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleChange}
                    placeholder="Patient Full Name"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <input
                    type="number"
                    name="patientAge"
                    value={formData.patientAge}
                    onChange={handleChange}
                    placeholder="Age"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 px-4 py-3"
                  >
                    <option value="">Gender</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Mobile Number"
                    inputMode="numeric"
                    maxLength={10}
                    pattern="[0-9]{10}"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  {mobileError ? (
                    <p className="mt-[-0.5rem] text-sm text-red-600 md:col-span-2">
                      {mobileError}
                    </p>
                  ) : null}
                  {/* <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Preferred Visit Timing
                    </label>
                    <input
                      type="datetime-local"
                      name="visitTiming"
                      value={formData.visitTiming}
                      onChange={handleVisitTimingChange}
                      onKeyDown={preventManualDateEntry}
                      onPaste={(event) => event.preventDefault()}
                      onDrop={(event) => event.preventDefault()}
                      min={formatDateTimeLocal(new Date())}
                      inputMode="none"
                      readOnly={formData.bookingType === "home"}
                      aria-readonly={formData.bookingType === "home"}
                      className={`w-full rounded-xl border border-gray-300 px-4 py-3 ${formData.bookingType === "home" ? "bg-gray-100 text-gray-700" : ""
                        }`}
                    />
                    {formData.bookingType === "home" ? (
                      <p className="mt-1 text-xs text-gray-500">
                        Auto-filled from the home visit schedule step.
                      </p>
                    ) : formData.bookingType === "daily" ? (
                      <p className="mt-1 text-xs text-gray-500">
                        Choose a future date and time only. Manual typing is disabled.
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        Choose the date and time in one picker.
                      </p>
                    )}
                  </div> */}
                  {/* preffered Timing */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Preferred Visit Timing
                    </label>
                  <div className="relative">
                    {!formData.visitTiming && (
                      <span className="pointer-events-none absolute inset-y-0 left-4 z-10 flex items-center text-gray-500">
                        Choose date and time
                      </span>
                    )}

                    <input
                      type="datetime-local"
                      name="visitTiming"
                      value={formData.visitTiming}
                      onChange={handleVisitTimingChange}
                      onKeyDown={preventManualDateEntry}
                      onPaste={(event) => event.preventDefault()}
                      onDrop={(event) => event.preventDefault()}
                      min={homeVisitMinTiming}
                      onClick={(event) => event.currentTarget.showPicker?.()}
                      className={`date-time-input w-full cursor-pointer rounded-xl border border-gray-300 px-4 py-3 ${!formData.visitTiming ? "date-time-empty" : ""
                        }`}
                      required
                    />
                  </div>
                  </div>
                  {/* <select
                    name="visitPrice"
                    value={formData.visitPrice}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  >
                    <option value="">Select Visit Charges</option>
                    <option value="500">₹500</option>
                    <option value="1100">₹1100</option>
                  </select> */}
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Symptoms / Notes"
                    className="rounded-xl border border-gray-300 px-4 py-3 md:col-span-2"
                  />
                  <div className="rounded-2xl border border-gray-200 bg-cream p-4">
                    <h3 className="text-lg font-semibold text-maroon">Charges</h3>
                    <p className="mt-2 text-sm text-gray-700">Consultation + care coordination starting from</p>
                    <p className="mt-2 text-2xl font-bold text-maroon">
                      Rs. {displayedVisitPrice}/-
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {currentStepKey === "Confirm" ? (
              <div>
                <h2 className="text-2xl font-bold text-maroon">Review & Confirm</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <div className="h-full rounded-2xl border border-gray-200 bg-cream p-4">
                    <h3 className="font-semibold text-maroon">Booking Summary</h3>
                    <ul className="mt-3 space-y-1 text-sm text-gray-700">
                      <li>Type: {formData.bookingType === "daily" ? "Daily Consultant" : "Home Visit"}</li>
                      {/* <li>Doctor: {selectedDoctor?.name || formData.specialist || "-"}</li> */}
                      {/* <li>Department: {formData.department || formData.specialist || "-"}</li>
                      <li>Date: {formData.date || "To be assigned"}</li> */}
                      {/* <li>Slot: {formData.slot || formData.visitTiming || "-"}</li> */}
                      <li>Mode: {formData.consultationMode}</li>
                      <li>Visit Price: Rs. {formData.visitPrice}</li>
                    </ul>
                  </div>

                  <div className="h-full rounded-2xl border border-gray-200 bg-cream p-4 ">
                    <h3 className="font-semibold text-maroon">Patient Summary</h3>
                    <ul className="mt-3 space-y-1 text-sm text-gray-700">
                      <li>Name: {formData.patientName}</li>
                      <li>Age: {formData.patientAge}</li>
                      <li>Gender: {formData.gender || "-"}</li>
                      <li>Mobile: {formData.mobile}</li>
                      {formData.bookingType === "home" ? (
                        <li>
                          Address: {formData.addressLine}, {formData.locality}, {formData.city} - {formData.pincode}
                        </li>
                      ) : null}
                    </ul>
                  </div>

                  <div className="h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm i" >
                    <h3 className="font-semibold text-maroon">Scanner</h3>
                    <p className="mt-2 text-sm text-gray-600">
                      Scan this image to proceed with payment or verification.
                    </p>
                    <div className="mt-4 flex justify-center overflow-hidden rounded-2xl border border-gray-200 bg-cream p-3">
                      <img
                        src="/ImageAndVideo/image/Scanner.jpeg"
                        alt="Scanner"
                        className="h-auto max-h-65 w-full max-w-[340px] rounded-xl object-contain"
                      />
                    </div>
                  </div>

                </div>
              </div>
            ) : null}

            <div className="mt-8 hidden items-center justify-between md:flex">
              <button
                type="button"
                onClick={handleBack}
                disabled={stepIndex === 0}
                className="rounded-full border border-maroon px-5 py-2 text-sm font-semibold text-maroon disabled:opacity-40"
              >
                Back
              </button>

              {stepIndex < stepLabels.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="rounded-full bg-maroon px-6 py-2 text-sm font-semibold text-white"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="rounded-full bg-maroon px-6 py-2 text-sm font-semibold text-white"
                >
                  Confirm Booking
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </SectionWrapper>

      <div className="fixed inset-x-0 bottom-0 z-[55] border-t border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            disabled={stepIndex === 0}
            className="flex-1 rounded-full border border-maroon px-4 py-3 text-sm font-semibold text-maroon disabled:opacity-40"
          >
            Back
          </button>
          {stepIndex < stepLabels.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex-1 rounded-full bg-maroon px-4 py-3 text-sm font-semibold text-white"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              className="flex-1 rounded-full bg-maroon px-4 py-3 text-sm font-semibold text-white"
            >
              Confirm
            </button>
          )}
        </div>
      </div>

      <Modal
        isOpen={successOpen}
        onClose={() => setSuccessOpen(false)}
        title="Appointment Request Submitted"
        maxWidth="max-w-md"
      >
        <p className="text-sm leading-relaxed text-gray-700">
          Thank you. Your consultation request has been captured successfully. Our care coordinator will confirm the doctor allocation and slot shortly.
        </p>
      </Modal>
    </main>
  );
};

export default BookDoctorConsultantPage;
